import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import Modal from "react-modal";
import { NumericFormat } from "react-number-format";
import IOnUpdateOrderEntryDetailDTO from "../../../models/DTO/IOnUpdateOrderEntryDetailDTO";
import ITaxJarCalculateSalesTaxDTO from "../../../models/DTO/ITaxJarCalculateSalesTaxDTO";
import IUpdateOrderEntryDetailDTO from "../../../models/DTO/IUpdateOrderEntryDetailDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOrder from "../../../models/IOrder";
import OrderSourceEnum from "../../../models/OrderSourceEnum";
import PriceUpchargeTypeEnum from "../../../models/PriceUpchargeTypeEnum";
import UserTypesEnum from "../../../models/UserTypesEnum";
import UIService from "../../../services/UIService";
import OwButton from "../../shared/OwButton/OwButton";
import OwEditModeToggle from "../../shared/OwEditModeToggle/OwEditModeToggle";
import OwInputWrapper from "../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../shared/OwLabel/OwLabel";
import "./OrderTotalsPanel.scss";
import taxJarDarkLogo from "../../../assets/images/taxjar/taxjar-logotype-dark.svg";
import taxJarLightLogo from "../../../assets/images/taxjar/taxjar-logotype.svg";
import TaxJarTaxBreakdownModal from "../TaxJarTaxBreakdownModal/TaxJarTaxBreakdownModal";
import { ChRobinsonRatesModal } from "../../shared/ChRobinsonRatesModal/ChRobinsonRatesModal";
import OrderEntryService from "../../../services/OrderEntryService";
import PromoCodeService from "../../../services/PromoCodeService";
import OrderStatesEnum from "../../../models/OrderStatesEnum";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import IApplyOrderPromoCodeDTO from "../../../models/DTO/IApplyOrderPromoCodeDTO";
import IRemoveOrderPromoCodeDTO from "../../../models/DTO/IRemoveOrderPromoCodeDTO";
import IOnApplyOrderPromoCodeDTO from "../../../models/DTO/IOnApplyOrderPromoCodeDTO";
import { OwPromoCodeInput } from "../../shared/OwPromoCodeInput/OwPromoCodeInput";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import { useAppContextNonNull } from "@contexts/AppContext";
import { OwContinueConfirmModal } from "@components/shared/OwContinueConfirmModal/OwContinueConfirmModal";

type Props = {
    shouldHidePricingFn: () => boolean;
    order: IOrder;
    orderItemType: string;
    isEcommerceView?: boolean;
    isReadOnly?: boolean;
    shouldHideTaxJarName?: boolean;
    lineView?: boolean;
    orderTotalWeight?: number;
    doCurrencyConversion?: boolean;
    className?: string;
};

export const OrderTotalsPanel = (props: Props) => {
    const { supplierModel, userModel, socket, uiContext } = useAppContextNonNull();

    const determineTaxShipping = () => {
        if (typeof props.order.Vendor?.tax_shipping === "boolean") {
            return props.order.Vendor?.tax_shipping;
        } else if (typeof supplierModel.sales_tax_config?.shippingTaxable === "boolean") {
            return supplierModel.sales_tax_config?.shippingTaxable;
        } else {
            return false;
        }
    };

    const [labor, setLabor] = useState<number | undefined>(props.order.labor);
    const [shipping, setShipping] = useState<number | undefined>(props.order.shipping);
    const [discount, setDiscount] = useState<number | undefined>(props.order.discount);
    const [discountTypeId, setDiscountTypeId] = useState<PriceUpchargeTypeEnum | undefined>(props.order.discount_type_id);
    const [taxRateOverride, setTaxRateOverride] = useState<number | undefined>(
        props.order.tax_rate_override ? props.order.tax_rate_override * 100 : undefined
    );
    const [laborEditMode, setLaborEditMode] = useState<boolean>(false);
    const [shippingEditMode, setShippingEditMode] = useState<boolean>(false);
    const [discountEditMode, setDiscountEditMode] = useState<boolean>(false);
    const [taxRateEditMode, setTaxRateEditMode] = useState<boolean>(false);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [modalComponent, setModalComponent] = useState<any>(null);
    const [modalStyle, setModalStyle] = useState<Modal.Styles | undefined>(undefined);
    const [promoApplying, setPromoApplying] = useState<boolean>(false);
    const [promoErrorKey, setPromoErrorKey] = useState<string | null>(null);
    const taxShipping = determineTaxShipping();

    const isMainViewLight = UIService.isMainViewLight();
    const rowClass = props.isEcommerceView ? "w-100 d-flex total-row" : "row";
    const colClass = props.isEcommerceView ? "w-50 detail-col" : "col-6";

    // Negative sum of credit lines; order.sub_total is already net of credits
    const creditTotal = (props.order.Order_Items || [])
        .filter((item) => item.is_credit)
        .reduce((sum, item) => sum + OrderEntryService.calculateRowTotal(props.order, item, supplierModel), 0);

    const onUpdateOrderEntryDetail = (data: IOnUpdateOrderEntryDetailDTO) => {
        if (!laborEditMode) {
            if (labor !== data.order_detail.labor) {
                setLabor(data.order_detail.labor);
            }
        }

        if (!shippingEditMode) {
            if (shipping !== data.order_detail.shipping) {
                setShipping(data.order_detail.shipping);
            }
        }

        if (!taxRateEditMode) {
            if (data.order_detail.tax_rate_override !== undefined) {
                setTaxRateOverride(data.order_detail.tax_rate_override ? data.order_detail.tax_rate_override * 100 : undefined);
            }
        }
    };

    /**
     * Apply / remove result. An invalid code is a normal result (`validation.valid === false`) that
     * leaves the order untouched; OrderEntry reloads the order on success, this panel only owns the
     * inline reason + busy state.
     */
    const onPromoCodeResult = (data: IOnApplyOrderPromoCodeDTO) => {
        setPromoApplying(false);

        if (data.validation && data.validation.valid === false) {
            setPromoErrorKey(PromoCodeService.getErrorMessageKey(data.validation.reason));
        } else {
            setPromoErrorKey(null);
        }
    };

    // Any Notify while an apply / remove is in flight means the request failed (the API's fallback
    // codes ERROR_APPLYING_PROMO_CODE / ERROR_REMOVING_PROMO_CODE do not carry the ERROR_PROMO_CODE_
    // prefix), so always clear the busy state. Only ERROR_PROMO_CODE_* (not enabled, order no longer
    // PRELIMINARY, ...) is also surfaced inline as its lower-cased, translated key.
    const onPromoCodeNotify = (data: IErrorDTO) => {
        setPromoApplying(false);

        if (PromoCodeService.isPromoCodeNotifyMessage(data?.message)) {
            setPromoErrorKey(data.message.toLowerCase());
        }
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.OrderEntryOnUpdateOrderEntryDetail, fn: onUpdateOrderEntryDetail },
        { endpoint: EndpointsEnum.OrderEntryOnApplyPromoCode, fn: onPromoCodeResult },
        { endpoint: EndpointsEnum.OrderEntryOnRemovePromoCode, fn: onPromoCodeResult },
        { endpoint: EndpointsEnum.Notify, fn: onPromoCodeNotify },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const handleChange = (e: ChangeEvent<any>) => {
        const name = e.target.name;
        const value = e.target.value;

        switch (name) {
            case "labor":
                setLabor(value);
                break;
            case "shipping":
                setShipping(value);
                break;
            case "discount":
                setDiscount(value);
                break;
            case "tax_rate_override":
                setTaxRateOverride(value);
                break;
        }
    };

    const cancelEditMode = (prop: "labor" | "shipping" | "discount" | "tax_rate_override") => {
        switch (prop) {
            case "labor":
                setLaborEditMode(!laborEditMode);
                break;
            case "shipping":
                setShippingEditMode(!shippingEditMode);
                break;
            case "discount":
                setDiscountEditMode(!discountEditMode);
                break;
            case "tax_rate_override":
                setTaxRateEditMode(!taxRateEditMode);
                break;
        }
    };

    const saveEdit = (prop: "labor" | "shipping" | "discount" | "tax_rate_override") => {
        const payload: Partial<IUpdateOrderEntryDetailDTO> = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuid: props.order.guid,
        };

        if (prop === "labor") {
            payload.labor = labor;
        } else if (prop === "shipping") {
            payload.shipping = shipping;
        } else if (prop === "discount") {
            payload.discount = discount;
            payload.discount_type_id = discountTypeId;
        } else if (prop === "tax_rate_override") {
            // Convert percentage to decimal (e.g., 7.5% becomes 0.075)
            payload.tax_rate_override = taxRateOverride ? taxRateOverride / 100 : null;
        }

        socket.emit(EndpointsEnum.OrderEntryUpdateOrderEntryDetail, payload);

        setLaborEditMode(false);
        setShippingEditMode(false);
        setDiscountEditMode(false);
        setTaxRateEditMode(false);
    };

    const resetManualShippingFee = () => {
        const payload: Partial<IUpdateOrderEntryDetailDTO> = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuid: props.order.guid,
            clear_manual_shipping_fee: true,
        };

        socket.emit(EndpointsEnum.OrderEntryUpdateOrderEntryDetail, payload);
        closeModal();
    };

    const confirmResetShippingFee = () => {
        const modalComponent = (
            <OwContinueConfirmModal message="confirm_reset_manual_shipping_fee" acceptFn={resetManualShippingFee} cancelFn={closeModal} />
        );

        setModalComponent(modalComponent);
        setModalIsOpen(true);
    };

    const toggleEditMode = (prop: "labor" | "shipping" | "discount" | "tax_rate_override") => {
        switch (prop) {
            case "labor":
                setLaborEditMode(!laborEditMode);
                break;
            case "shipping":
                setShippingEditMode(!shippingEditMode);
                break;
            case "discount":
                setDiscountEditMode(!discountEditMode);
                break;
            case "tax_rate_override":
                setTaxRateEditMode(!taxRateEditMode);
                break;
        }
    };

    const toggleBtn = (activeType: PriceUpchargeTypeEnum) => {
        setDiscountTypeId(activeType);
    };

    const getDiscountTotal = () => {
        const activeClassName = "active btn-primary btn";

        if (!supplierModel.has_discounts) {
            return null;
        }

        if (props.lineView) {
            return (
                <>
                    <tr className={"text-end"}>
                        <td>
                            <OwLabel title={"discount"} isBold={true} />
                        </td>
                        <td>
                            <NumericFormat
                                value={props.order.discount_total}
                                displayType={"text"}
                                thousandSeparator={true}
                                decimalScale={supplierModel.pricing_precision || 2}
                                fixedDecimalScale={true}
                                suffix={")"}
                                prefix={"($"}
                            />
                        </td>
                    </tr>
                    <tr className={"text-end"}>
                        <td>
                            <OwLabel title={"discounted_sub_total"} noBottomMargin={true} isBold={true} />
                        </td>
                        <td>
                            <NumericFormat
                                value={
                                    (props.order!.sub_total || 0) -
                                    (props.order!.discount_total || 0) -
                                    (props.order!.promo_discount_total || 0)
                                }
                                displayType={"text"}
                                thousandSeparator={true}
                                decimalScale={supplierModel.pricing_precision || 2}
                                fixedDecimalScale={true}
                                prefix={"$"}
                            />
                        </td>
                    </tr>
                </>
            );
        } else {
            return (
                <React.Fragment>
                    {
                        <div className={"discount-total " + rowClass}>
                            <span className={colClass}>
                                <div className={"d-flex justify-content-end"}>
                                    {!props.isReadOnly &&
                                    userModel.isSupplierUser &&
                                    (userModel.userType?.id === UserTypesEnum.ADMIN || supplierModel.allow_non_admin_discount) &&
                                    (props.order.order_source_id === OrderSourceEnum.ORDERWERKS ||
                                        props.order.order_source_id === OrderSourceEnum.APP) ? (
                                        <OwEditModeToggle
                                            className={"me-1"}
                                            editMode={discountEditMode}
                                            cancelEditFn={() => {
                                                cancelEditMode("discount");
                                            }}
                                            saveEditFn={() => {
                                                saveEdit("discount");
                                            }}
                                            toggleEditFn={() => {
                                                toggleEditMode("discount");
                                            }}
                                        />
                                    ) : null}
                                    <OwLabel title={"discount"} noBottomMargin={true} />
                                    {props.order.discount_type_id === PriceUpchargeTypeEnum.FIXED
                                        ? "(-)"
                                        : props.order.discount_type_id === PriceUpchargeTypeEnum.PERCENT
                                          ? "(%)"
                                          : ""}
                                </div>
                                <div>
                                    {discountEditMode ? (
                                        <div className={"input-group mt-1"}>
                                            <OwInputWrapper name={"discount"} fn={handleChange} value={discount} />
                                            {/*<div className={"input-group-append"}>*/}
                                            <OwButton
                                                hoverTitle={"Discount Percent"}
                                                className={
                                                    "w-3r " +
                                                    (discountTypeId === PriceUpchargeTypeEnum.PERCENT ? activeClassName : "btn-primary")
                                                }
                                                centerIconClass={"fas fa-percent"}
                                                fn={() => {
                                                    toggleBtn(PriceUpchargeTypeEnum.PERCENT);
                                                }}
                                            />

                                            <OwButton
                                                hoverTitle={"Discount Amount"}
                                                className={
                                                    "w-3r " +
                                                    (discountTypeId === PriceUpchargeTypeEnum.FIXED ? activeClassName : "btn-primary")
                                                }
                                                centerIconClass={"fas fa-minus"}
                                                fn={() => {
                                                    toggleBtn(PriceUpchargeTypeEnum.FIXED);
                                                }}
                                            />
                                            {/*</div>*/}
                                        </div>
                                    ) : props.order.discount_type_id === PriceUpchargeTypeEnum.FIXED ? (
                                        <NumericFormat
                                            value={props.order.discount}
                                            displayType={"text"}
                                            thousandSeparator={true}
                                            decimalScale={supplierModel.pricing_precision || 2}
                                            fixedDecimalScale={true}
                                            prefix={"$"}
                                        />
                                    ) : (
                                        <NumericFormat
                                            value={props.order.discount}
                                            displayType={"text"}
                                            thousandSeparator={true}
                                            decimalScale={0}
                                            fixedDecimalScale={true}
                                            suffix={"%"}
                                        />
                                    )}
                                </div>
                            </span>
                            <span className={props.isEcommerceView ? colClass : "col-6"}>
                                <NumericFormat
                                    value={props.order.discount_total}
                                    displayType={"text"}
                                    thousandSeparator={true}
                                    decimalScale={supplierModel.pricing_precision || 2}
                                    fixedDecimalScale={true}
                                    suffix={")"}
                                    prefix={"($"}
                                />
                            </span>
                        </div>
                    }

                    {!supplierModel.has_discounts ? null : (
                        <div className={rowClass}>
                            <span className={colClass}>
                                <OwLabel title={"discounted_sub_total"} noBottomMargin={true} />
                            </span>
                            <span className={colClass + " .g-0"}>
                                <NumericFormat
                                    value={
                                        (props.order!.sub_total || 0) -
                                        (props.order!.discount_total || 0) -
                                        (props.order!.promo_discount_total || 0)
                                    }
                                    displayType={"text"}
                                    thousandSeparator={true}
                                    decimalScale={supplierModel.pricing_precision || 2}
                                    fixedDecimalScale={true}
                                    prefix={"$"}
                                />
                            </span>
                        </div>
                    )}
                </React.Fragment>
            );
        }
    };

    const applyPromoCode = (code: string) => {
        const payload: IApplyOrderPromoCodeDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuid: props.order.guid,
            code,
        };

        setPromoApplying(true);
        setPromoErrorKey(null);
        socket.emit(EndpointsEnum.OrderEntryApplyPromoCode, payload);
    };

    const removePromoCode = () => {
        const payload: IRemoveOrderPromoCodeDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuid: props.order.guid,
        };

        setPromoApplying(true);
        setPromoErrorKey(null);
        socket.emit(EndpointsEnum.OrderEntryRemovePromoCode, payload);
    };

    /**
     * Promo code row: stacks with (is separate from) the manual discount above it. Entry is only
     * offered on PRELIMINARY orders to supplier users when the supplier has promo codes enabled; an
     * order that already carries a code always shows it read-only (the discount is frozen on the order).
     */
    const getPromoCode = () => {
        const hasPromo = !!props.order.promo_code_id || !!props.order.promo_code;
        const canEdit =
            !props.isReadOnly &&
            userModel.isSupplierUser &&
            !!supplierModel.has_promo_codes &&
            props.order.order_state_id === OrderStatesEnum.PRELIMINARY;

        if (!hasPromo && !canEdit) {
            return null;
        }

        // Applied: the badge (+ remove) sits inline after the panel's own label. Entering: the input
        // goes on its own line beneath the label.
        const promoInput = (
            <OwPromoCodeInput
                appliedCode={props.order.promo_code}
                onApply={applyPromoCode}
                onRemove={removePromoCode}
                errorKey={promoErrorKey}
                applying={promoApplying}
                canEdit={canEdit}
                pricingPrecision={supplierModel.pricing_precision || 2}
                isSmall={true}
                showLabel={false}
            />
        );

        const promoLabel = (
            <div className={"d-flex justify-content-end align-items-center gap-1 flex-wrap"}>
                <OwLabel title={"promo_code"} noBottomMargin={true} isBold={!!props.lineView} />
                {hasPromo ? promoInput : null}
            </div>
        );

        const promoEntry = !hasPromo && canEdit ? <div className={"mt-1"}>{promoInput}</div> : null;

        const promoAmount = (
            <NumericFormat
                value={props.order.promo_discount_total || 0}
                displayType={"text"}
                thousandSeparator={true}
                decimalScale={supplierModel.pricing_precision || 2}
                fixedDecimalScale={true}
                suffix={")"}
                prefix={"($"}
            />
        );

        if (props.lineView) {
            return (
                <tr className={"text-end promo-code-total"}>
                    <td>
                        {promoLabel}
                        {promoEntry}
                    </td>
                    <td>{promoAmount}</td>
                </tr>
            );
        }

        return (
            <div className={"promo-code-total " + rowClass}>
                <span className={colClass}>
                    {promoLabel}
                    {promoEntry}
                </span>
                <span className={props.isEcommerceView ? colClass : "col-6"}>{promoAmount}</span>
            </div>
        );
    };

    const openChRobinsonRates = () => {
        const _modalComponent = (
            <ChRobinsonRatesModal
                weight={props.orderTotalWeight || 0}
                order={props.order}
                closeModalFn={closeModal}
                setActivityIndicator={uiContext.setActivityIndicator}
                notifyFn={uiContext.notifyFn}
                socket={socket}
            />
        );

        setModalComponent(_modalComponent);
        setModalIsOpen(true);
        setModalStyle({ content: { width: "auto", minWidth: "20vw" } });
    };

    const getShippingFeeTotal = () => {
        if (supplierModel.has_shipping_fee || supplierModel.has_shipping_by_weight) {
            if (props.lineView) {
                return (
                    <tr className={"text-end"}>
                        <td>
                            <OwLabel title={"shipping"} isBold={true} />
                        </td>
                        <td>
                            <NumericFormat
                                value={props.order.shipping}
                                displayType={"text"}
                                thousandSeparator={true}
                                decimalScale={2}
                                fixedDecimalScale={true}
                                prefix={"$"}
                            />
                        </td>
                    </tr>
                );
            } else {
                let shippingTotal = props.order.shipping || 0;

                let prefix = "$";

                if (props.doCurrencyConversion) {
                    let currency = props.order?.Catalog?.Currency ? props.order.Catalog.Currency : undefined;

                    if (props.order?.Currency) {
                        currency = props.order.Currency;
                    }

                    prefix = currency ? OrderEntryService.getCurrencySymbol(currency.name) : "$";

                    if (currency && props.order?.Catalog?.Currency) {
                        shippingTotal = OrderEntryService.convertCurrency(
                            shippingTotal,
                            props.order?.Catalog?.Currency,
                            currency,
                            props.order?.exchange_rate
                        );
                    }
                }

                return (
                    <div className={"shipping-total " + rowClass}>
                        <span className="col-6 d-flex justify-content-end">
                            {!props.isReadOnly && supplierModel.Supplier_Integration.has_chrobinson ? (
                                <span className={"me-4"}>
                                    <OwButton
                                        title={props.order.Order_Detail?.chrobinson_rate_detail ? "view_detail" : "get_rates"}
                                        fn={openChRobinsonRates}
                                    />
                                </span>
                            ) : null}
                            {!props.isReadOnly && userModel.isSupplierUser && props.order.is_manual_shipping_fee ? (
                                <span className="me-2">
                                    <OwButton
                                        className="btn-warning"
                                        centerIconClass="fas fa-rotate-left"
                                        hoverTitle="Reset Manual Shipping Fee"
                                        fn={confirmResetShippingFee}
                                    />
                                </span>
                            ) : null}
                            {!props.isReadOnly &&
                            userModel.isSupplierUser &&
                            !supplierModel.pass_through_shipping_costs &&
                            (props.order.order_source_id === OrderSourceEnum.ORDERWERKS ||
                                props.order.order_source_id === OrderSourceEnum.APP) ? (
                                <OwEditModeToggle
                                    className={"me-1"}
                                    editMode={shippingEditMode}
                                    cancelEditFn={() => {
                                        cancelEditMode("shipping");
                                    }}
                                    saveEditFn={() => {
                                        saveEdit("shipping");
                                    }}
                                    toggleEditFn={() => {
                                        toggleEditMode("shipping");
                                    }}
                                />
                            ) : null}
                            <FormattedMessage id="shipping" defaultMessage="Shipping" description="Shipping Title" />
                        </span>
                        <span className={colClass}>
                            {shippingEditMode ? (
                                <OwInputWrapper name={"shipping"} value={shipping} fn={handleChange} />
                            ) : (
                                <NumericFormat
                                    value={shippingTotal}
                                    displayType={"text"}
                                    thousandSeparator={true}
                                    decimalScale={2}
                                    fixedDecimalScale={true}
                                    prefix={prefix}
                                />
                            )}
                        </span>
                    </div>
                );
            }
        } else {
            return null;
        }
    };

    const closeModal = () => {
        setModalComponent(null);
        setModalIsOpen(false);
        setModalStyle(undefined);
    };

    const viewTaxJarDetail = () => {
        const _modalComponent = <TaxJarTaxBreakdownModal closeModalFn={closeModal} orderModel={props.order} />;

        setModalComponent(_modalComponent);
        setModalIsOpen(true);
    };

    const confirmUpdateTaxJarModal = () => {
        const modalComponent = (
            <OwContinueConfirmModal
                message={"confirm_updating_tax_calculations_from_taxjar"}
                acceptFn={updateTaxJarDetail}
                cancelFn={closeModal}
            />
        );

        setModalComponent(modalComponent);
        setModalIsOpen(true);
    };

    const updateTaxJarDetail = () => {
        const payload: ITaxJarCalculateSalesTaxDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuid: props.order.guid,
        };

        if (!userModel.isSupplierUser) {
            payload.vendorGuid = props.order.Vendor!.guid;
        }

        socket.emit(EndpointsEnum.TaxJarCalculateSalesTax, payload);
    };

    const getCreditCardSurcharge = () => {
        if (supplierModel.has_credit_card_surcharge && props.order.Vendor?.require_payment) {
            if (props.lineView) {
                return (
                    <tr className={"text-end"}>
                        <td>
                            <OwLabel title={"credit_card_fee"} isBold={true} />({supplierModel.credit_card_surcharge} %)
                        </td>
                        <td>
                            <NumericFormat
                                value={props.order.credit_card_surcharge_amount}
                                displayType={"text"}
                                thousandSeparator={true}
                                decimalScale={supplierModel.pricing_precision || 2}
                                fixedDecimalScale={true}
                                prefix={"$"}
                            />
                        </td>
                    </tr>
                );
            } else {
                return (
                    <div className={"credit-card-surcharge-total " + rowClass}>
                        <span className="col-6 d-flex justify-content-end">
                            <FormattedMessage id="credit_card_fee" defaultMessage="Credit Card Fee" description="Credit Card Fee Title" />
                        </span>
                        <span className={colClass}>
                            <NumericFormat
                                value={props.order.credit_card_surcharge_amount}
                                displayType={"text"}
                                thousandSeparator={true}
                                decimalScale={2}
                                fixedDecimalScale={true}
                                prefix={"$"}
                            />
                        </span>
                    </div>
                );
            }
        }
    };

    const getTaxTotal = () => {
        if (supplierModel.settings.screens?.order_entry?.hide_tax_total) {
            return null;
        }

        let taxJarLogo = taxJarDarkLogo;

        if (isMainViewLight) {
            taxJarLogo = taxJarLightLogo;
        }

        let taxRate = supplierModel.tax_rate || 0;

        if (props.order.Vendor?.Item_Sales_Tax) {
            taxRate = props.order.Vendor?.Item_Sales_Tax.rate;
        }

        if (supplierModel.sales_tax_config) {
            if (supplierModel.sales_tax_config.usePerStateTax) {
                if (supplierModel.sales_tax_config.includedStates[props.order.Ship_To?.state || ""]) {
                    taxRate = supplierModel.sales_tax_config.includedStates[props.order.Ship_To?.state || ""];
                }
            }
        }

        if (props.order.tax_rate_override) {
            // Convert from decimal to percentage for display (e.g., 0.075 to 7.5)
            // Round to 2 decimal places to avoid floating point precision issues
            taxRate = Math.round(props.order.tax_rate_override * 10000) / 100;
        }

        let totalTax = props.order!.tax || 0;

        let prefix = "$";

        if (props.doCurrencyConversion) {
            let currency = props.order?.Catalog?.Currency ? props.order.Catalog.Currency : undefined;

            if (props.order?.Currency) {
                currency = props.order.Currency;
            }

            prefix = currency ? OrderEntryService.getCurrencySymbol(currency.name) : "$";

            if (currency && props.order?.Catalog?.Currency) {
                totalTax = OrderEntryService.convertCurrency(
                    totalTax,
                    props.order?.Catalog?.Currency,
                    currency,
                    props.order?.exchange_rate
                );
            }
        }

        let allowManualTax = false;

        if (userModel.isSupplierUser && supplierModel.sales_tax_config) {
            if (userModel.supplierUserToken?.User_Type?.id === UserTypesEnum.ADMIN) {
                allowManualTax = supplierModel.sales_tax_config.allowAdminManualTax || false;
            } else {
                allowManualTax = supplierModel.sales_tax_config.allowSalesUserManualTax || false;
            }
        }

        if (props.lineView) {
            return (
                <tr className={"text-end"}>
                    <td className={"text-end no-wrap"}>
                        <label>
                            <OwLabel title={"tax"} isBold={true} />
                            {props.order!.order_source_id === OrderSourceEnum.ORDERWERKS ||
                            props.order!.order_source_id === OrderSourceEnum.APP ? (
                                props.order.Vendor!.tax_exempt ? null : (
                                    <span className={"ms-1"}>
                                        ({taxRate} %)
                                        {props.order.tax_rate_override ? (
                                            <span className={"text-danger ms-1"} title="Tax rate manually overridden">
                                                *
                                            </span>
                                        ) : null}
                                    </span>
                                )
                            ) : null}
                        </label>
                    </td>
                    <td className={"text-end"}>
                        <NumericFormat
                            value={totalTax}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={2}
                            fixedDecimalScale={true}
                            prefix={prefix}
                        />
                    </td>
                </tr>
            );
        } else {
            return (
                <div className={"tax-total align-items-center no-wrap " + rowClass}>
                    <span className={colClass}>
                        {!supplierModel.Supplier_Integration.has_taxjar ? (
                            <div className={"d-flex justify-content-end align-items-center"}>
                                {!props.isReadOnly &&
                                userModel.isSupplierUser &&
                                !props.order.Vendor?.tax_exempt &&
                                allowManualTax &&
                                (props.order.order_source_id === OrderSourceEnum.ORDERWERKS ||
                                    props.order.order_source_id === OrderSourceEnum.APP) ? (
                                    <OwEditModeToggle
                                        className={"me-1"}
                                        editMode={taxRateEditMode}
                                        cancelEditFn={() => {
                                            cancelEditMode("tax_rate_override");
                                        }}
                                        saveEditFn={() => {
                                            saveEdit("tax_rate_override");
                                        }}
                                        toggleEditFn={() => {
                                            toggleEditMode("tax_rate_override");
                                        }}
                                    />
                                ) : null}
                                <div>
                                    <FormattedMessage id="tax" defaultMessage="Tax" description="Tax Title" />
                                    {props.order!.order_source_id === OrderSourceEnum.ORDERWERKS ||
                                    props.order!.order_source_id === OrderSourceEnum.APP ? (
                                        props.order.Vendor!.tax_exempt ? null : (
                                            <>
                                                {taxRateEditMode ? (
                                                    <span className={"ms-1"}>(%)</span>
                                                ) : (
                                                    <span className={"ms-1"}>
                                                        ({taxRate} %)
                                                        {props.order.tax_rate_override ? (
                                                            <span className={"text-danger ms-1"} title="Tax rate manually overridden">
                                                                *
                                                            </span>
                                                        ) : null}
                                                    </span>
                                                )}
                                            </>
                                        )
                                    ) : null}
                                </div>
                            </div>
                        ) : null}
                        {supplierModel.Supplier_Integration.has_taxjar ? (
                            <div className={"d-flex align-items-end justify-content-end"}>
                                {userModel.isSupplierUser && (
                                    <OwLabel
                                        className={"fas fa-refresh pointer text-primary taxJarDetails me-2 d-print-none"}
                                        fn={props.order.taxjar_calc_tax_resp ? confirmUpdateTaxJarModal : updateTaxJarDetail}
                                        noBottomMargin={true}
                                    />
                                )}
                                {props.order.taxjar_calc_tax_resp &&
                                    (userModel.isSupplierUser && userModel.supplierUserToken?.User_Type?.id === UserTypesEnum.ADMIN ? (
                                        <OwLabel
                                            description="details"
                                            className={"fas fa-magnifying-glass pointer text-primary taxJarDetails me-3 d-print-none"}
                                            fn={viewTaxJarDetail}
                                            noBottomMargin={true}
                                        />
                                    ) : null)}
                                {props.shouldHideTaxJarName ? (
                                    <OwLabel title={"tax"} />
                                ) : (
                                    <img src={taxJarLogo} style={{ height: "1rem" }} />
                                )}
                            </div>
                        ) : null}
                    </span>
                    <span className={colClass}>
                        {taxRateEditMode ? (
                            <OwInputWrapper name={"tax_rate_override"} value={taxRateOverride} fn={handleChange} placeholder="e.g., 7.5" />
                        ) : (
                            <NumericFormat
                                value={totalTax}
                                displayType={"text"}
                                thousandSeparator={true}
                                decimalScale={2}
                                fixedDecimalScale={true}
                                prefix={prefix}
                            />
                        )}
                    </span>
                </div>
            );
        }
    };

    const getGrandTotal = () => {
        let grandTotal = props.order!.grand_total || 0;

        let prefix = "$";

        if (props.doCurrencyConversion) {
            let currency = props.order?.Catalog?.Currency ? props.order.Catalog.Currency : undefined;

            if (props.order?.Currency) {
                currency = props.order.Currency;
            }

            prefix = currency ? OrderEntryService.getCurrencySymbol(currency.name) : "$";

            if (currency && props.order?.Catalog?.Currency) {
                grandTotal = OrderEntryService.convertCurrency(
                    grandTotal,
                    props.order?.Catalog?.Currency,
                    currency,
                    props.order?.exchange_rate
                );
            }
        }

        if (props.lineView) {
            return (
                <tr className={"text-end"}>
                    <td className={"no-wrap"}>
                        <OwLabel title={"grand_total"} isBold={true} />
                    </td>
                    <td>
                        <NumericFormat
                            value={grandTotal}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={supplierModel.pricing_precision || 2}
                            fixedDecimalScale={true}
                            prefix={prefix}
                        />
                    </td>
                </tr>
            );
        } else {
            return (
                <div className={"no-wrap grand-total " + rowClass}>
                    <span className={colClass}>
                        <FormattedMessage id="grand_total" defaultMessage="Grand Total" description="Grand Total Title" />
                    </span>
                    <span className={colClass}>
                        <NumericFormat
                            value={grandTotal}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={supplierModel.pricing_precision || 2}
                            fixedDecimalScale={true}
                            prefix={prefix}
                        />
                    </span>
                </div>
            );
        }
    };

    const getMsrpGrandTotal = () => {
        if (!supplierModel.settings.screens?.order_entry?.show_msrp_total) {
            return null;
        }

        let grandTotal = 0;

        for (const item of props.order.Order_Items) {
            const msrp = item.msrp || item.Catalog_Item?.msrp || 0;

            if (item.Catalog_Item?.msrp) {
                if (item.msrp) {
                    grandTotal += msrp * item.qty;
                } else {
                    grandTotal += msrp * item.qty;
                }
            }
        }

        let prefix = "$";

        if (props.doCurrencyConversion) {
            let currency = props.order?.Catalog?.Currency ? props.order.Catalog.Currency : undefined;

            if (props.order?.Currency) {
                currency = props.order.Currency;
            }

            prefix = currency ? OrderEntryService.getCurrencySymbol(currency.name) : "$";

            if (currency && props.order?.Catalog?.Currency) {
                grandTotal = OrderEntryService.convertCurrency(
                    grandTotal,
                    props.order?.Catalog?.Currency,
                    currency,
                    props.order?.exchange_rate
                );
            }
        }

        if (props.lineView) {
            return (
                <tr className={"text-end"}>
                    <td className={"no-wrap"}>
                        <OwLabel title={"msrp_grand_total"} />
                    </td>
                    <td>
                        <NumericFormat
                            value={grandTotal}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={supplierModel.pricing_precision || 2}
                            fixedDecimalScale={true}
                            prefix={prefix}
                        />
                    </td>
                </tr>
            );
        } else {
            return (
                <div className={"no-wrap msrp-grand-total " + rowClass}>
                    <span className={colClass}>
                        <FormattedMessage id="msrp_grand_total" defaultMessage="MSRP Grand Total" description="MSRP Grand Total Title" />
                    </span>
                    <span className={colClass}>
                        <NumericFormat
                            value={grandTotal}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={supplierModel.pricing_precision || 2}
                            fixedDecimalScale={true}
                            prefix={prefix}
                        />
                    </span>
                </div>
            );
        }
    };

    const getCreditTotal = () => {
        if (!creditTotal) {
            return null;
        }

        let credit = creditTotal;

        let prefix = "$";

        if (props.doCurrencyConversion) {
            let currency = props.order?.Catalog?.Currency ? props.order.Catalog.Currency : undefined;

            if (props.order?.Currency) {
                currency = props.order.Currency;
            }

            prefix = currency ? OrderEntryService.getCurrencySymbol(currency.name) : "$";

            if (currency && props.order?.Catalog?.Currency) {
                credit = OrderEntryService.convertCurrency(credit, props.order?.Catalog?.Currency, currency, props.order?.exchange_rate);
            }
        }

        if (props.lineView) {
            return (
                <tr className={"text-end text-danger"}>
                    <td className={"no-wrap"}>
                        <OwLabel title={"credit"} isBold={true} />
                    </td>
                    <td>
                        <NumericFormat
                            value={Math.abs(credit)}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={supplierModel.pricing_precision || 2}
                            fixedDecimalScale={true}
                            prefix={"(" + prefix}
                            suffix={")"}
                        />
                    </td>
                </tr>
            );
        } else {
            return (
                <div className={(props.isEcommerceView ? rowClass : "row ") + " credit-total no-wrap text-danger"}>
                    <span className={colClass}>
                        <OwLabel title={"credit"} noBottomMargin={true} />
                    </span>
                    <span className={colClass}>
                        <NumericFormat
                            value={Math.abs(credit)}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={supplierModel.pricing_precision || 2}
                            fixedDecimalScale={true}
                            prefix={"(" + prefix}
                            suffix={")"}
                        />
                    </span>
                </div>
            );
        }
    };

    const getSubTotal = () => {
        // Displayed gross of credits (purchases only); the Credit row below shows the difference.
        // The stored sub_total stays net of credits.
        let subTotal = (props.order!.sub_total || 0) - creditTotal;

        let prefix = "$";

        if (props.doCurrencyConversion) {
            let currency = props.order?.Catalog?.Currency ? props.order.Catalog.Currency : undefined;

            if (props.order?.Currency) {
                currency = props.order.Currency;
            }

            prefix = currency ? OrderEntryService.getCurrencySymbol(currency.name) : "$";

            if (currency && props.order?.Catalog?.Currency) {
                subTotal = OrderEntryService.convertCurrency(
                    subTotal,
                    props.order?.Catalog?.Currency,
                    currency,
                    props.order?.exchange_rate
                );
            }
        }

        if (props.lineView) {
            return (
                <tr className={"text-end"}>
                    <td className={"no-wrap"}>
                        <OwLabel title={"sub_total"} isBold={true} />
                    </td>
                    <td>
                        <NumericFormat
                            value={subTotal}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={supplierModel.pricing_precision || 2}
                            fixedDecimalScale={true}
                            prefix={prefix}
                        />
                    </td>
                </tr>
            );
        } else {
            return (
                <div className={(props.isEcommerceView ? rowClass : "row ") + " sub-total no-wrap"}>
                    <span className={colClass}>
                        <FormattedMessage id="sub_total" defaultMessage="Sub Total" description="Sub Total Title" />
                    </span>
                    <span className={colClass}>
                        <NumericFormat
                            value={subTotal}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={supplierModel.pricing_precision || 2}
                            fixedDecimalScale={true}
                            prefix={prefix}
                        />
                    </span>
                </div>
            );
        }
    };

    const getLaborFee = () => {
        if (!supplierModel.has_labor_fee) {
            return null;
        }

        if (props.lineView) {
            return (
                <tr className={"text-end"}>
                    <td>
                        <OwLabel title={"labor"} isBold={true} />
                    </td>
                    <td>
                        <NumericFormat
                            value={props.order.labor}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={2}
                            fixedDecimalScale={true}
                            prefix={"$"}
                        />
                    </td>
                </tr>
            );
        } else {
            return (
                <div className={rowClass + " labor-total"}>
                    <span className={colClass + " d-flex justify-content-end"}>
                        {!props.isReadOnly && userModel.isSupplierUser ? (
                            <OwEditModeToggle
                                className={"me-1"}
                                editMode={laborEditMode}
                                cancelEditFn={() => {
                                    cancelEditMode("labor");
                                }}
                                saveEditFn={() => {
                                    saveEdit("labor");
                                }}
                                toggleEditFn={() => {
                                    toggleEditMode("labor");
                                }}
                            />
                        ) : null}
                        <FormattedMessage id="labor" defaultMessage="Labor" description="Labor Title" />
                    </span>
                    <span className={colClass}>
                        {laborEditMode ? (
                            <OwInputWrapper name={"labor"} value={labor} fn={handleChange} />
                        ) : (
                            <NumericFormat
                                value={props.order.labor}
                                displayType={"text"}
                                thousandSeparator={true}
                                decimalScale={2}
                                fixedDecimalScale={true}
                                prefix={"$"}
                            />
                        )}
                    </span>
                </div>
            );
        }
    };

    const getStandardOrderItemTotals = () => {
        if (props.shouldHidePricingFn()) {
            return null;
        }

        let className = props.isEcommerceView ? "total-box w-100" : "text-end total-box container";

        if (props.lineView) {
            className = "table table-bordered";
        }

        if (props.lineView) {
            return (
                <table className={"table table-bordered mb-0 " + (isMainViewLight ? "" : "table-dark")}>
                    <tbody>
                        {getSubTotal()}
                        {getCreditTotal()}
                        {getDiscountTotal()}
                        {getPromoCode()}
                        {taxShipping ? null : getTaxTotal()}
                        {getLaborFee()}
                        {getShippingFeeTotal()}
                        {taxShipping ? getTaxTotal() : null}
                        {getCreditCardSurcharge()}
                        {getGrandTotal()}
                        {getMsrpGrandTotal()}
                    </tbody>
                </table>
            );
        } else {
            return (
                <div className={className} id={"orderTotalsBox"}>
                    {getSubTotal()}

                    {getCreditTotal()}

                    {getDiscountTotal()}

                    {getPromoCode()}

                    {taxShipping ? null : getTaxTotal()}

                    {getLaborFee()}

                    {getShippingFeeTotal()}

                    {taxShipping ? getTaxTotal() : null}

                    {getCreditCardSurcharge()}

                    {getGrandTotal()}

                    {getMsrpGrandTotal()}

                    <div>
                        <Modal
                            isOpen={modalIsOpen}
                            contentLabel="Orderwerks"
                            shouldCloseOnOverlayClick={false}
                            className={"modalContentClass"}
                            overlayClassName={"modalOverlayClass"}
                            style={modalStyle}>
                            {modalComponent}
                        </Modal>
                    </div>
                </div>
            );
        }
    };

    const getTotals = () => {
        switch (props.order.order_item_type_id) {
            default:
                return getStandardOrderItemTotals();
        }
    };

    if (!props.order) {
        return null;
    }

    let className = "OrderTotalsPanel d-inline-flex mb-5";

    if (props.className) {
        className += " " + props.className;
    } else {
        let minWidth = "min-w-15r";

        if (props.order.is_manual_shipping_fee) {
            minWidth = "min-w-20r";
        }

        className += " " + (props.isEcommerceView ? "w-100" : minWidth);
    }

    return <div className={className}>{getTotals()}</div>;
};
